/** Pip easter egg — categories, wardrobe, challenges (see pip-final.html + cursor-handoff). */

export const PIP_CREDIT_REWARD = 50;
export const PIP_SPAWN_CHANCE = 1 / 40;
export const PIP_COOLDOWN_MS = 4 * 60 * 60 * 1000;
export const PIP_VISIBLE_MS = 20_000;
export const PIP_DWELL_MS = 60_000;

/**
 * Routes where Pip may randomly appear. Never on drop detail or studio chrome.
 * Exact `/` plus prefix matches for the rest.
 */
export const PIP_SPAWN_ROUTES = [
  "/",
  "/home",
  "/following",
  "/gallery",
  "/discover",
  "/perks",
];

export function isPipSpawnRoute(pathname = "") {
  if (!pathname) return false;
  // Never on drop detail / post detail
  if (pathname.startsWith("/drop/") || pathname.startsWith("/post/")) return false;
  return PIP_SPAWN_ROUTES.some((route) => {
    if (route === "/") return pathname === "/" || pathname === "/home";
    return pathname === route || pathname.startsWith(`${route}/`);
  });
}

export const PIP_TOTAL_CATEGORIES = 60;

/** Spawn types: random | always | bypass | event | scroll | dwell | time | contextual | page */
function cat(id, opts = {}) {
  return {
    id,
    spawnType: "random",
    state: "idle",
    peek: null,
    anchor: null,
    routes: null,
    event: null,
    ...opts,
  };
}

export const PIP_CATEGORIES = [
  cat("CAT_001", { anchor: "nav-logo", state: "peek", peek: "A", spawnType: "random" }),
  cat("CAT_002", { anchor: "nav-bell", state: "idle", spawnType: "random" }),
  cat("CAT_003", { anchor: "nav-search", state: "peek", peek: "C", spawnType: "random" }),
  cat("CAT_004", { anchor: "search-input", state: "peek", peek: "B", routes: ["/search"], spawnType: "random" }),
  cat("CAT_005", { anchor: "nav-avatar", state: "peek", peek: "A", spawnType: "random" }),
  cat("CAT_006", { anchor: "nav-home-gallery", state: "idle", spawnType: "random" }),
  cat("CAT_007", { anchor: "nav-chevron", state: "peek", peek: "B", spawnType: "random" }),
  cat("CAT_008", { anchor: "drop-card", state: "peek", peek: "C", spawnType: "random" }),
  cat("CAT_009", { anchor: "creator-avatar", state: "idle", spawnType: "random" }),
  cat("CAT_010", { anchor: "niche-pill", state: "idle", spawnType: "random" }),
  cat("CAT_011", { anchor: "drop-lock", state: "peek", peek: "A", spawnType: "random" }),
  cat("CAT_012", { anchor: "view-count", state: "idle", spawnType: "random" }),
  cat("CAT_013", { anchor: "closing-badge", state: "peek", peek: "B", spawnType: "random" }),
  cat("CAT_014", { anchor: "drop-price", state: "idle", spawnType: "random" }),
  cat("CAT_015", { anchor: "first100-banner", state: "wave", spawnType: "random", routes: ["/drop"] }),
  cat("CAT_016", { anchor: "tip-jar", state: "idle", spawnType: "random" }),
  cat("CAT_017", { anchor: "featured-hero", state: "peek", peek: "A", routes: ["/featured"], spawnType: "random" }),
  cat("CAT_018", { anchor: "perks-balance", state: "idle", routes: ["/perks"], spawnType: "random" }),
  cat("CAT_019", { anchor: "perks-theme", state: "peek", peek: "C", routes: ["/perks"], spawnType: "random" }),
  cat("CAT_020", { anchor: "nom-rank", state: "idle", routes: ["/featured/community"], spawnType: "random" }),
  cat("CAT_021", { anchor: "share-modal", state: "idle", spawnType: "random" }),
  cat("CAT_022", { anchor: "games-pip", state: "idle", routes: ["/games"], spawnType: "always" }),
  cat("CAT_023", { anchor: "landing-logo", state: "peek", peek: "A", routes: ["/welcome"], spawnType: "random" }),
  cat("CAT_024", { anchor: "waitlist-email", state: "peek", peek: "B", routes: ["/waitlist"], spawnType: "random" }),
  cat("CAT_025", { anchor: "fan-badge", state: "idle", spawnType: "random" }),
  cat("CAT_026", { spawnType: "scroll", state: "wave", anchor: "page-footer" }),
  cat("CAT_027", { spawnType: "dwell", state: "idle" }),
  cat("CAT_028", { spawnType: "event", event: "pull_refresh", state: "wave" }),
  cat("CAT_029", { spawnType: "bypass", event: "like_drop", state: "found", duration: 3000 }),
  cat("CAT_030", { spawnType: "bypass", event: "wall_note_sent", state: "idle", duration: 4000 }),
  cat("CAT_031", { spawnType: "bypass", event: "nominate_creator", state: "wave", duration: 3000 }),
  cat("CAT_032", { spawnType: "bypass", event: "tip_success", state: "found", duration: 4000 }),
  cat("CAT_033", { spawnType: "bypass", event: "founding_badge_hover", state: "peek", peek: "A", duration: 3000 }),
  cat("CAT_034", { spawnType: "bypass", event: "profile_complete", state: "wave", duration: 4000 }),
  cat("CAT_035", { spawnType: "bypass", event: "streak_7", state: "found", duration: 4000 }),
  cat("CAT_036", { spawnType: "time", timeRule: "midnight_hour", state: "idle" }),
  cat("CAT_037", { spawnType: "time", timeRule: "sunday", state: "wave" }),
  cat("CAT_038", { spawnType: "time", timeRule: "first_of_month", state: "found" }),
  cat("CAT_039", { spawnType: "time", timeRule: "canada_day", state: "found", outfit: "challenge_canadaday" }),
  cat("CAT_040", { spawnType: "time", timeRule: "anniversary", state: "wave" }),
  cat("CAT_041", { spawnType: "contextual", contextRule: "views_remaining_50", state: "peek", peek: "B", routes: ["/drop"] }),
  cat("CAT_042", { spawnType: "contextual", contextRule: "inactive_7d", state: "wave" }),
  cat("CAT_043", { spawnType: "contextual", contextRule: "first_visit_page", state: "peek", peek: "A" }),
  cat("CAT_044", { spawnType: "contextual", contextRule: "viewer_rank_50", state: "found", routes: ["/drop"] }),
  cat("CAT_045", { spawnType: "always", state: "idle", routes: ["404"], speech: "even Pip gets lost sometimes" }),
  cat("CAT_046", { anchor: "mobile-bottom-nav", state: "peek", peek: "B", spawnType: "random" }),
  cat("CAT_047", { anchor: "mobile-menu", state: "idle", spawnType: "random" }),
  cat("CAT_048", { anchor: "mobile-toast", state: "peek", peek: "C", spawnType: "random" }),
  cat("CAT_049", { anchor: "mobile-nav-gap", state: "idle", spawnType: "random" }),
  cat("CAT_050", { anchor: "earnings-graph", state: "idle", routes: ["/earnings", "/analytics"], spawnType: "random" }),
  cat("CAT_051", { anchor: "dashboard-sidebar", state: "peek", peek: "C", spawnType: "random" }),
  cat("CAT_052", { anchor: "payout-amount", state: "idle", routes: ["/earnings"], spawnType: "random" }),
  cat("CAT_053", { anchor: "draft-drop", state: "peek", peek: "A", spawnType: "random" }),
  cat("CAT_054", { anchor: "video-play", state: "idle", routes: ["/drop"], spawnType: "random" }),
  cat("CAT_055", { anchor: "video-progress", state: "idle", routes: ["/drop"], spawnType: "random" }),
  cat("CAT_056", { anchor: "comments-input", state: "peek", peek: "B", routes: ["/drop"], spawnType: "random" }),
  cat("CAT_057", { anchor: "drop-description", state: "peek", peek: "B", routes: ["/drop"], spawnType: "random" }),
  cat("CAT_058", { anchor: "drop-tags", state: "idle", routes: ["/drop"], spawnType: "random" }),
  cat("CAT_059", { anchor: "gallery-niche-filter", state: "idle", routes: ["/gallery"], spawnType: "random" }),
  cat("CAT_060", { anchor: "gallery-featured-hero", state: "peek", peek: "A", routes: ["/gallery"], spawnType: "random" }),
];

export const PIP_CATEGORY_MAP = Object.fromEntries(
  PIP_CATEGORIES.map((c) => [c.id, c])
);

export const PIP_CHALLENGE_OUTFITS = {
  challenge_10finds: { slot: "accessory", label: "Silver magnifying glass", unlockAt: 10 },
  challenge_25finds: { slots: ["hat", "shirt"], label: "Detective coat & hat", unlockAt: 25 },
  challenge_50finds: { slot: "accessory", label: "Gold monocle", unlockAt: 50 },
  challenge_100finds: { slots: ["hat", "shirt", "pants", "shoes"], label: "Full gold suit", unlockAt: 100 },
  challenge_streak7: { slot: "hat", label: "Flame hat", unlockAt: 7, streak: true },
  challenge_canadaday: { slot: "accessory", label: "Maple leaf cape", canadaDay: true },
  challenge_allcategories: { slot: "hat", label: "Pip Master crown", allCategories: true },
  challenge_founder: { slots: ["hat", "shirt", "pants", "shoes"], label: "Founding Fan gold", founder: true },
};

function item(id, slot, cost, label, opts = {}) {
  return { id, slot, cost, label, ...opts };
}

export const PIP_WARDROBE_CATALOG = [
  item("hat_toque", "hat", 150, "Red & white toque"),
  item("hat_beret", "hat", 150, "French Canadian beret"),
  item("hat_hockey", "hat", 150, "Hockey helmet"),
  item("hat_hardhat", "hat", 150, "Construction hard hat"),
  item("hat_cowboy", "hat", 150, "Cowboy hat"),
  item("hat_graduation", "hat", 150, "Graduation cap"),
  item("hat_crown", "hat", 300, "Gold crown"),
  item("hat_birthday", "hat", 300, "Birthday hat", { anniversaryOnly: true }),
  item("shirt_100vo", "shirt", 150, "100ViewsOnly logo tee"),
  item("shirt_flannel", "shirt", 150, "Red flannel"),
  item("shirt_chef", "shirt", 150, "Chef coat"),
  item("shirt_hockey", "shirt", 150, "Hockey jersey"),
  item("shirt_creator", "shirt", 300, "Director shirt"),
  item("shirt_vintage", "shirt", 300, "Vintage band tee"),
  item("pants_jeans", "pants", 100, "Jeans"),
  item("pants_hockey", "pants", 100, "Hockey shorts"),
  item("pants_pjs", "pants", 100, "Flannel pyjamas"),
  item("pants_overalls", "pants", 100, "Overalls"),
  item("pants_maple", "pants", 100, "Maple leaf shorts"),
  item("shoes_skates", "shoes", 100, "Hockey skates"),
  item("shoes_boots", "shoes", 100, "Snow boots"),
  item("shoes_sneakers", "shoes", 100, "Sneakers"),
  item("shoes_flipflops", "shoes", 100, "Flip flops"),
  item("shoes_workboots", "shoes", 100, "Work boots"),
  item("acc_coffee", "accessory", 100, "Tim Hortons cup"),
  item("acc_maple", "accessory", 100, "Maple syrup bottle"),
  item("acc_backpack", "accessory", 100, "Backpack"),
  item("acc_sunglasses", "accessory", 100, "Sunglasses"),
  item("acc_scarf", "accessory", 100, "Scarf"),
  item("acc_hockeystick", "accessory", 100, "Hockey stick"),
  item("set_wellness", "set", 500, "Wellness set", { setId: "wellness" }),
  item("set_beauty", "set", 500, "Beauty set", { setId: "beauty" }),
  item("set_food", "set", 500, "Food set", { setId: "food" }),
  item("set_fitness", "set", 500, "Fitness set", { setId: "fitness" }),
  item("set_auto", "set", 500, "Auto set", { setId: "auto" }),
  item("set_music", "set", 500, "Music set", { setId: "music" }),
  item("set_arts", "set", 500, "Arts set", { setId: "arts" }),
];

export const PIP_WARDROBE_MAP = Object.fromEntries(
  PIP_WARDROBE_CATALOG.map((i) => [i.id, i])
);

export const PIP_DEFAULT_OUTFIT = {
  hat: null,
  shirt: null,
  pants: null,
  shoes: null,
  accessory: null,
};

export const PIP_INTERACTIVE_ANIMS = ["wave", "jump", "spin", "wink", "dance"];

export function pipStorageKey() {
  return "pipLastFound";
}

export function isPipOnCooldown(lastFoundMs = 0) {
  if (!lastFoundMs) return false;
  return Date.now() - lastFoundMs < PIP_COOLDOWN_MS;
}

export function nowET() {
  return new Date(new Date().toLocaleString("en-US", { timeZone: "America/Toronto" }));
}

export function matchesTimeRule(rule, userCreatedAt, context = {}) {
  const d = nowET();
  if (rule === "midnight_hour") return d.getHours() === 0;
  if (rule === "sunday") return d.getDay() === 0;
  if (rule === "first_of_month") return d.getDate() === 1;
  if (rule === "canada_day") return d.getMonth() === 6 && d.getDate() === 1;
  if (rule === "anniversary" && userCreatedAt) {
    const created = userCreatedAt?.seconds
      ? new Date(userCreatedAt.seconds * 1000)
      : new Date(userCreatedAt);
    const c = new Date(created.toLocaleString("en-US", { timeZone: "America/Toronto" }));
    return c.getMonth() === d.getMonth() && c.getDate() === d.getDate();
  }
  if (rule === "views_remaining_50") return context.viewsRemaining === 50;
  if (rule === "inactive_7d") return context.inactiveDays >= 7;
  if (rule === "first_visit_page") return context.firstVisitPage === true;
  if (rule === "viewer_rank_50") return context.viewerRank === 50;
  return false;
}

export function matchesRoute(category, pathname) {
  if (pathname === "/404" || category.routes?.includes("404")) {
    return category.routes?.includes("404");
  }
  if (!category.routes) return true;
  return category.routes.some(
    (r) => pathname === r || pathname.startsWith(`${r}/`)
  );
}
